'use client';

import { useState } from 'react';
import { Textarea } from '@workspace/ui/components/textarea';
import { noBorderStyles } from '@/src/constants/constants';
import { IBitComment } from 'types/src/types';
import { useBoolean } from '@/src/hooks/use-boolean';
import { createBitComment } from '@/src/actions/comments';
import { Button } from '@workspace/ui/components/button';
import { Spinner } from '@/src/components/ui';

type Props = {
  bitId: number;
  replyToComment?: IBitComment;
  placeholder?: string;
  onCommentPosted?: () => void;
};

export const CreateComment = ({
  bitId,
  replyToComment,
  placeholder,
  onCommentPosted,
}: Props) => {
  const [comment, setComment] = useState('');
  const isLoading = useBoolean(false);

  const handleSubmit = async () => {
    if (!comment.trim()) return;

    isLoading.onTrue();
    try {
      if (replyToComment) {
        await createBitComment(
          { content: comment, replyTo: replyToComment.id },
          bitId
        );
        onCommentPosted?.();
      } else {
        await createBitComment({ content: comment }, bitId);
        onCommentPosted?.();
      }
      setComment('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      isLoading.onFalse();
    }
  };

  return (
    <div className="relative">
      <Textarea
        style={noBorderStyles}
        placeholder={placeholder ?? 'Write your comment...'}
        className="rounded-md p-2 md:p-4 bg-bg-gray sm:placeholder:font-normal md:placeholder:text-base sm:placeholder:text-sm resize-none min-h-36 max-h-72 sm:text-sm md:text-base text-black md:pr-16 overflow-scroll"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      {comment.trim() && (
        <Button
          type="button"
          size="sm"
          className="absolute md:right-4 md:top-4 top-1 right-1 px-2 py-1"
          onClick={handleSubmit}
        >
          {isLoading.value ? <Spinner /> : 'Post'}
        </Button>
      )}
    </div>
  );
};
